import { AreaExtensions } from 'rete-area-plugin';
import type { TaskNode } from './task-node';
import type { setupPlugins } from './plugins';

type Area = ReturnType<typeof setupPlugins>['area'];

type NodeBounds = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  width: number;
  height: number;
};

const VIEW_PADDING = 96;
const DEFAULT_ZOOM_RANGE = { min: 0.2, max: 2.6 };
const ZOOM_FIT_PADDING = 0.9;

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

export function createViewControls(
  area: Area,
  container: HTMLElement,
  taskNodes: Map<number, TaskNode>
) {
  const getContainerRect = () => {
    const rect = container.getBoundingClientRect();
    if (rect.width <= 1 || rect.height <= 1) return null;
    return rect;
  };

  const getNodeBounds = (): NodeBounds | null => {
    const boxes: Array<{
      x: number;
      y: number;
      width: number;
      height: number;
    }> = [];

    for (const node of taskNodes.values()) {
      const view = area.nodeViews.get(String(node.id));
      if (!view || !view.position) continue;
      const width =
        (node as any).width ??
        (node as any).meta?.width ??
        view.element?.clientWidth ??
        200;
      const height =
        (node as any).height ??
        (node as any).meta?.height ??
        view.element?.clientHeight ??
        200;
      boxes.push({
        x: view.position.x,
        y: view.position.y,
        width,
        height,
      });
    }

    if (boxes.length === 0) return null;

    const minX = Math.min(...boxes.map((b) => b.x));
    const minY = Math.min(...boxes.map((b) => b.y));
    const maxX = Math.max(...boxes.map((b) => b.x + b.width));
    const maxY = Math.max(...boxes.map((b) => b.y + b.height));

    return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
  };

  const getFitZoom = (bounds: NodeBounds | null) => {
    const rect = getContainerRect();
    if (!bounds || !rect) return 1;
    const availableWidth = Math.max(rect.width - VIEW_PADDING * 2, 1);
    const availableHeight = Math.max(rect.height - VIEW_PADDING * 2, 1);
    const contentWidth = Math.max(bounds.width, 1);
    const contentHeight = Math.max(bounds.height, 1);
    return Math.min(availableWidth / contentWidth, availableHeight / contentHeight);
  };

  const getZoomRange = () => {
    const bounds = getNodeBounds();
    if (!bounds) return DEFAULT_ZOOM_RANGE;
    const fit = getFitZoom(bounds);
    const min = clamp(fit * 0.8, DEFAULT_ZOOM_RANGE.min, 1);
    const max = clamp(Math.max(2, fit * 3), min + 0.1, DEFAULT_ZOOM_RANGE.max);
    return { min, max };
  };

  const getTranslateRange = () => {
    const bounds = getNodeBounds();
    const rect = getContainerRect();
    if (!bounds || !rect) {
      const pad = VIEW_PADDING;
      return {
        left: -pad,
        right: pad,
        top: -pad,
        bottom: pad,
      };
    }
    const k = area.area.transform?.k ?? 1;
    const minTx = VIEW_PADDING - bounds.minX * k;
    const maxTx = rect.width - VIEW_PADDING - bounds.maxX * k;
    const minTy = VIEW_PADDING - bounds.minY * k;
    const maxTy = rect.height - VIEW_PADDING - bounds.maxY * k;
    return {
      left: Math.min(minTx, maxTx),
      right: Math.max(minTx, maxTx),
      top: Math.min(minTy, maxTy),
      bottom: Math.max(minTy, maxTy),
    };
  };

  AreaExtensions.restrictor(area, {
    scaling: () => getZoomRange(),
    translation: () => getTranslateRange(),
  });

  const centerOnContent = async () => {
    const nodes = Array.from(taskNodes.values());
    if (nodes.length === 0) {
      await area.area.translate(0, 0);
      await area.area.zoom(1, 0, 0);
      return;
    }
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
    );
    await AreaExtensions.zoomAt(area, nodes as any, { scale: ZOOM_FIT_PADDING });
  };

  return { centerOnContent };
}
